import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Table, Select, Input, Button, Alert, notification } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import BasicLayout from '../../components/layout/BasicLayout';
import { fetchBudgetMeta, fetchPeriods, fetchRows } from './api';
import RowFormModal from './RowFormModal';
import BalanceScreen from './BalanceScreen';
import ImportScreen from './ImportScreen';
import { fmtSum, fmtPct } from './format';
import './BudgetPage.css';

const { Option } = Select;

export default function BudgetEnteringPage() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || '').toLowerCase().startsWith('kk') ? 'kk' : 'ru';

  // Справочники
  const [meta, setMeta] = useState({ sections: [], budgets: [], tolerance: 0.001 });
  const secByKey = useMemo(() => {
    const m = {};
    meta.sections.forEach((s) => {
      m[s.k] = s;
    });
    return m;
  }, [meta.sections]);

  const [screen, setScreen] = useState('rows'); // rows | import | balance
  const [f, setF] = useState({ budget: '8', period: '2026-05-31', section: '', depth: '', q: '' });
  const [qInput, setQInput] = useState('');
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(20);
  const [periods, setPeriods] = useState([]);

  // Перечень
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [rowsRefresh, setRowsRefresh] = useState(0);
  const [balanceRefresh, setBalanceRefresh] = useState(0);

  // Форма строки
  const [modalOpen, setModalOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);

  // id текущего периода — нужен для periods/{id}/integrity (баланс), не дата.
  const periodId = useMemo(
    () => (periods.find((p) => p.date === f.period) || {}).id,
    [periods, f.period]
  );

  /* -------------------------------- meta ---------------------------------- */
  useEffect(() => {
    fetchBudgetMeta()
      .then((m) => setMeta(m))
      .catch(() => setLoadError(true));
  }, []);

  /* Смена бюджета → перезапросить периоды; если текущего периода нет — взять
     последний доступный; сбросить страницу на 1. */
  useEffect(() => {
    fetchPeriods(f.budget).then((list) => {
      setPeriods(list);
      setF((prev) => {
        const dates = list.map((p) => p.date);
        const period = dates.includes(prev.period) ? prev.period : dates[dates.length - 1] || '';
        return { ...prev, period };
      });
      setPage(1);
    });
  }, [f.budget]);

  /* Поиск — с задержкой 300 мс, без запроса на каждый символ. */
  useEffect(() => {
    const id = setTimeout(() => {
      setF((prev) => (prev.q === qInput ? prev : { ...prev, q: qInput }));
      setPage(1);
    }, 300);
    return () => clearTimeout(id);
  }, [qInput]);

  /* Перечень: серверная пагинация + фильтры. */
  useEffect(() => {
    if (!f.period) {
      setRows([]);
      setTotal(0);
      setLoading(false);
      return undefined;
    }
    let alive = true;
    setLoading(true);
    setLoadError(false);
    fetchRows({
      budget: f.budget,
      period: f.period,
      section: f.section,
      depth: f.depth,
      q: f.q,
      page,
      size,
    })
      .then(({ rows: r, total: tt }) => {
        if (alive) {
          setRows(r);
          setTotal(tt);
        }
      })
      .catch(() => {
        if (alive) setLoadError(true);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [f.budget, f.period, f.section, f.depth, f.q, page, size, rowsRefresh]);

  /* ------------------------------ фильтры ---------------------------------- */
  const setFilter = (patch) => {
    setF((prev) => ({ ...prev, ...patch }));
    setPage(1);
  };
  const resetFilters = () => {
    // очищает раздел, уровень, поиск — но не бюджет и период
    setQInput('');
    setF((prev) => ({ ...prev, section: '', depth: '', q: '' }));
    setPage(1);
  };

  const periodLabel = (p) => p.date + (p.is_partial ? ` (${t('budgetEntering.partial')})` : '');

  /* -------------------------------- колонки -------------------------------- */
  const columns = [
    {
      title: t('budgetEntering.colSection'),
      key: 'sec',
      width: 118,
      render: (_, r) => {
        const s = secByKey[r.sec] || { part: 'fin' };
        return <span className={`bp-tag ${s.part}`}>{r.sec}</span>;
      },
    },
    {
      title: t('budgetEntering.colCode'),
      key: 'code',
      width: 150,
      render: (_, r) =>
        r.code ? <span className="bp-code">{r.code}</span> : <span className="bp-noname"> </span>,
    },
    {
      title: t('budgetEntering.colName'),
      key: 'name',
      render: (_, r) => (
        <span>
          <span className="bp-lvl">{r.level}</span>
          {r.name_missing ? (
            <span className="bp-noname">{t('budgetEntering.noName')}</span>
          ) : (
            r.name
          )}
        </span>
      ),
    },
    {
      title: t('budgetEntering.colUb'),
      key: 'ub',
      align: 'right',
      width: 120,
      render: (_, r) => <span className="bp-num">{fmtSum(r.ub)}</span>,
    },
    {
      title: t('budgetEntering.colAdj'),
      key: 'adj',
      align: 'right',
      width: 120,
      render: (_, r) => <span className="bp-num">{fmtSum(r.adj)}</span>,
    },
    {
      title: t('budgetEntering.colPp'),
      key: 'pp',
      align: 'right',
      width: 120,
      render: (_, r) => <span className="bp-num">{fmtSum(r.pp)}</span>,
    },
    {
      title: t('budgetEntering.colEx'),
      key: 'ex',
      align: 'right',
      width: 120,
      render: (_, r) => <span className="bp-num">{fmtSum(r.ex)}</span>,
    },
    {
      title: t('budgetEntering.colP1'),
      key: 'p1',
      align: 'right',
      width: 90,
      render: (_, r) => <span className="bp-num">{fmtPct(r.pct)}</span>,
    },
    {
      title: '',
      key: 'act',
      width: 60,
      align: 'right',
      render: (_, r) => (
        <button className="bp-iact" title={t('budgetEntering.edit')} onClick={() => openRow(r)}>
          ✎
        </button>
      ),
    },
  ];

  const openRow = (row) => {
    setEditRow(row || null);
    setModalOpen(true);
  };
  const closeRow = () => {
    setModalOpen(false);
    setEditRow(null);
  };

  const afterSaved = useCallback(
    (res) => {
      closeRow();
      setRowsRefresh((n) => n + 1);
      setBalanceRefresh((n) => n + 1);
      notification.success({
        message: t('budgetEntering.toastSaved'),
        placement: 'bottomRight',
        description: (
          <a onClick={(e) => e.preventDefault()} href="#log">
            {t('budgetEntering.toLog')}
          </a>
        ),
      });
      if (res && res.warnings && res.warnings.length) {
        notification.warning({
          message: t('budgetEntering.toastWarnings'),
          placement: 'bottomRight',
          description: res.warnings
            .map((w) => (lang === 'kk' ? w.message_kk : w.message_ru) || w.message)
            .join('; '),
        });
      }
    },
    [t, lang]
  );

  const tabs = [
    { k: 'rows', label: t('budgetEntering.tRows') },
    { k: 'import', label: t('budgetEntering.tImport') },
    { k: 'balance', label: t('budgetEntering.tBalance') },
  ];

  return (
    <BasicLayout>
      <div className="bp-page">
        <div className="bp-head">
          <div>
            <h2 className="bp-title">{t('budgetEntering.pageTitle')}</h2>
          </div>
        </div>

        <div className="bp-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.k}
              className={`bp-tab${screen === tab.k ? ' on' : ''}`}
              onClick={() => setScreen(tab.k)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ================= ЭКРАН: СТРОКИ ОТЧЁТА ================= */}
        {screen === 'rows' && (
          <section>
            <div className="bp-card">
              <div className="bp-card-h">
                <h2>{t('budgetEntering.rowsTitle')}</h2>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => openRow(null)}>
                  {t('budgetEntering.createRow')}
                </Button>
              </div>
              <div className="bp-card-b">
                <div className="bp-filters">
                  <div className="bp-fl">
                    <label>{t('budgetEntering.fBudget')}</label>
                    <Select
                      value={f.budget}
                      onChange={(v) => setF((p) => ({ ...p, budget: v }))}
                      style={{ minWidth: 250 }}
                    >
                      {meta.budgets.map((b) => (
                        <Option key={b.k} value={b.k}>
                          {lang === 'kk' ? b.kk : b.ru}
                        </Option>
                      ))}
                    </Select>
                  </div>
                  <div className="bp-fl">
                    <label>{t('budgetEntering.fPeriod')}</label>
                    <Select
                      value={f.period || undefined}
                      onChange={(v) => setFilter({ period: v })}
                      style={{ minWidth: 160 }}
                    >
                      {periods.map((p) => (
                        <Option key={p.date} value={p.date}>
                          {periodLabel(p)}
                        </Option>
                      ))}
                    </Select>
                  </div>
                  <div className="bp-fl">
                    <label>{t('budgetEntering.fSection')}</label>
                    <Select
                      value={f.section || ''}
                      onChange={(v) => setFilter({ section: v })}
                      style={{ minWidth: 230 }}
                    >
                      <Option value="">{t('budgetEntering.allSections')}</Option>
                      {meta.sections.map((s) => (
                        <Option key={s.k} value={s.k}>
                          {lang === 'kk' ? s.kk : s.ru}
                        </Option>
                      ))}
                    </Select>
                  </div>
                  <div className="bp-fl">
                    <label>{t('budgetEntering.fDepth')}</label>
                    <Select
                      value={f.depth === '' ? '' : String(f.depth)}
                      onChange={(v) => setFilter({ depth: v })}
                      style={{ minWidth: 120 }}
                    >
                      <Option value="">{t('budgetEntering.anyLevel')}</Option>
                      {[0, 1, 2, 3, 4, 5, 6].map((d) => (
                        <Option key={d} value={String(d)}>
                          {d === 0
                            ? t('budgetEntering.lvlSection')
                            : t('budgetEntering.lvlN', { n: d })}
                        </Option>
                      ))}
                    </Select>
                  </div>
                  <div className="bp-fl" style={{ flex: 1, minWidth: 250 }}>
                    <label>{t('budgetEntering.fSearch')}</label>
                    <Input
                      value={qInput}
                      onChange={(e) => setQInput(e.target.value)}
                      placeholder={t('budgetEntering.searchPh')}
                      allowClear
                    />
                  </div>
                  <Button onClick={resetFilters}>{t('budgetEntering.reset')}</Button>
                </div>
              </div>

              {loadError ? (
                <div className="bp-card-b">
                  <Alert
                    type="warning"
                    showIcon
                    message={t('budgetEntering.loadError')}
                    action={
                      <Button size="small" onClick={() => setRowsRefresh((n) => n + 1)}>
                        {t('budgetEntering.retry')}
                      </Button>
                    }
                  />
                </div>
              ) : (
                <div className="bp-tbl-wrap">
                  <Table
                    className="bp-table"
                    rowKey="id"
                    columns={columns}
                    dataSource={rows}
                    loading={loading}
                    sticky
                    scroll={{ y: 460 }}
                    locale={{
                      emptyText: <div className="bp-empty">{t('budgetEntering.noRows')}</div>,
                    }}
                    pagination={{
                      current: page,
                      pageSize: size,
                      total,
                      showSizeChanger: true,
                      pageSizeOptions: ['10', '20', '50', '100'],
                      showTotal: (tot) => t('budgetEntering.totalRows', { n: tot }),
                      onChange: (p, ps) => {
                        if (ps !== size) {
                          setSize(ps);
                          setPage(1);
                        } else setPage(p);
                      },
                      onShowSizeChange: (_c, ps) => {
                        setSize(ps);
                        setPage(1);
                      },
                    }}
                  />
                </div>
              )}
            </div>
          </section>
        )}

        {/* ================= ЭКРАН: ЗАГРУЗКА ФАЙЛА ================= */}
        {screen === 'import' && (
          <ImportScreen
            budgets={meta.budgets}
            defaultBudget={f.budget}
            onImported={() => setRowsRefresh((n) => n + 1)}
          />
        )}

        {/* ================= ЭКРАН: КОНТРОЛЬ БАЛАНСА ================= */}
        {screen === 'balance' && (
          <BalanceScreen
            budget={f.budget}
            period={f.period}
            periodId={periodId}
            budgets={meta.budgets}
            refreshKey={balanceRefresh}
          />
        )}
      </div>

      {/* Модальная форма строки — одна страница, четыре блока. */}
      <RowFormModal
        open={modalOpen}
        editRow={editRow}
        sections={meta.sections}
        budget={f.budget}
        period={f.period}
        tolerance={meta.tolerance}
        onSaved={afterSaved}
        onClose={closeRow}
      />
    </BasicLayout>
  );
}
